import React from 'react';
import { Chip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button, Avatar } from '@heroui/react';
import { Icon } from '@iconify/react';
import { UniversalTable } from '../AccountTable';
import { RefShareOffer, RefShareOfferOwner, OfferStatus, OwnerLevel } from '../../api/services/refShareApi';
import Typography from '../typography/Typography';
import IdView from '../id-view';
import { useNavigate } from 'react-router';
import RevshareDetailsCell from '../../pages/revshare/offers/components/RevshareDetailsCell';
import StatisticsCell from '../../pages/revshare/offers/components/StatisticsCell';
import { useTranslations } from '../../contexts/translation.context';

const columns = [
  { key: 'id', name: 'ID оффера' },
  { key: 'owner', name: 'Владелец' },
  { key: 'model', name: 'Модель' },
  { key: 'offerDetails', name: 'Детали RevShare' },
  { key: 'statistics', name: 'Статистика' },
  { key: 'status', name: 'Статус' },
  { key: 'actions', name: '' },
];

const getStatusConfig = (status: OfferStatus) => {
  switch (status) {
    case 'active':
      return {
        label: 'Активный',
        dotColor: '#17c964',
      };
    case 'hidden':
      return {
        label: 'Скрыт',
        dotColor: '#71717A',
      };
    case 'moderation':
      return {
        label: 'На модерации',
        dotColor: '#E8A323',
      };
    case 'refusal':
      return {
        label: 'Отклонен',
        dotColor: '#ef4444',
      };
    default:
      return {
        label: status,
        dotColor: '#71717A',
      };
  }
};

const getLevelConfig = (lvl?: OwnerLevel) => {
  if (!lvl) return null;

  switch (lvl.toLowerCase()) {
    case 'new':
      return {
        label: 'New',
        bgColor: 'rgba(23, 201, 100, 0.2)',
        textColor: '#17c964',
      };
    case 'base':
      return {
        label: 'Base',
        bgColor: '#EEEEEF',
        textColor: '#71717A',
      };
    case 'pro':
      return {
        label: 'PRO',
        bgColor: '#11181C',
        textColor: '#FFFFFF',
      };
    case 'expert':
      return {
        label: 'Expert',
        bgColor: 'rgba(120, 40, 200, 0.2)',
        textColor: '#7828c8',
      };
    case 'bronze':
      return { label: 'Bronze', bgColor: '#FDF4E7', textColor: '#CD7F32' };
    case 'silver':
      return { label: 'Silver', bgColor: '#F0F0F0', textColor: '#808080' };
    case 'gold':
      return { label: 'Gold', bgColor: '#FFF9E6', textColor: '#FFD700' };
    case 'platinum':
      return { label: 'Platinum', bgColor: '#E8F4F8', textColor: '#A0B2C6' };
    case 'diamond':
      return { label: 'Diamond', bgColor: '#E6F3FF', textColor: '#00BFFF' };
    default:
      return {
        label: lvl,
        bgColor: '#EEEEEF',
        textColor: '#71717A',
      };
  }
};

const getOwnerData = (owner: RefShareOfferOwner | string | undefined): RefShareOfferOwner | null => {
  if (!owner) return null;
  if (typeof owner === 'string') return null;
  return owner;
};

interface DashboardRevShareOffersTableProps {
  offers: RefShareOffer[];
  onStatusChange?: (modelId: string, status: OfferStatus) => void;
}

const DashboardRevShareOffersTable: React.FC<DashboardRevShareOffersTableProps> = ({ offers, onStatusChange }) => {
  const navigate = useNavigate();
  const { t } = useTranslations();

  const handleAction = (action: string, modelId: string) => {
    switch (action) {
      case 'deactivate':
        onStatusChange?.(modelId, 'hidden');
        break;
      case 'activate':
        onStatusChange?.(modelId, 'active');
        break;
    }
  };

  const renderCell = (offer: RefShareOffer, columnKey: string) => {
    const offerId = offer._id || offer.id || '';
    const ownerData = getOwnerData(offer.owner);
    const offerStatus = offer.offer?.status || offer.status || 'moderation';

    switch (columnKey) {
      case 'id':
        return (
          <IdView textToCopy={offerId}>
            {offerId.slice(-4)}
          </IdView>
        );

      case 'owner': {
        if (!ownerData) {
          return <span className="text-[#71717A]">—</span>;
        }

        const ownerId = ownerData._id || ownerData.id || '';
        const ownerType = ownerData.type || 'creator';
        const href = `/users/creators/${ownerId}`;
        const levelConfig = getLevelConfig(ownerData.lvl);

        return (
          <div className="flex items-center gap-[12px]">
            <Avatar
              src={ownerData.avatar}
              showFallback
              name={ownerData.name?.charAt(0)}
              className="w-[40px] h-[40px] rounded-[12px] bg-gray-200 uppercase text-[14px] font-medium"
              classNames={{
                fallback: "bg-gray-200 text-gray-600"
              }}
            />
            <div className="flex flex-col gap-[4px]">
              <span
                onClick={() => navigate(href)}
                className="text-[#006FEE] text-[14px] font-semibold hover:underline cursor-pointer"
              >
                {ownerData.name}
              </span>
              <div className="flex items-center gap-[6px]">
                {levelConfig && (
                  <span
                    className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium"
                    style={{
                      backgroundColor: levelConfig.bgColor,
                      color: levelConfig.textColor,
                    }}
                  >
                    {levelConfig.label}
                  </span>
                )}
                {/* Type Badge - Creator/Agency */}
                <span
                  className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium"
                  style={{
                    backgroundColor: ownerType === 'agency' ? 'rgba(120, 40, 200, 0.2)' : 'rgba(0, 111, 238, 0.2)',
                    color: ownerType === 'agency' ? '#7828C8' : '#006FEE'
                  }}
                >
                  {ownerType === 'agency' ? t('admin.creators.type.agency') : t('admin.creators.type.creator')}
                </span>
              </div>
            </div>
          </div>
        );
      }

      case 'model': {
        const modelName = offer.name || offer.of_user_name || '';
        const modelUsername = offer.of_user_name || '';
        const popularityPercent = offer.of_data?.overall_popularity_percent;
        const onlyfansLink = modelUsername ? `https://onlyfans.com/${modelUsername}` : '';

        return (
          <div className="flex items-center gap-[12px]">
            <Avatar
              src={offer.avatar}
              showFallback
              name={modelName?.charAt(0)}
              className="w-[40px] h-[40px] rounded-[12px] bg-gray-200"
              classNames={{
                fallback: "bg-gray-200 text-gray-600"
              }}
            />
            <div className="flex flex-col gap-[2px]">

                <Typography variant="body1" className="font-semibold text-[#11181C]">
                  {modelName}
                </Typography>

              <div className="flex items-center gap-[6px]">
                {modelUsername && (
                <a
                  href={onlyfansLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#006FEE] text-[14px] font-semibold hover:underline"
                >
                  @{modelUsername}
                </a>

                )}
                {popularityPercent !== undefined && popularityPercent !== null && (
                  <Chip
                    classNames={{
                      base: 'h-[18px] px-[6px] bg-[#F5A524]/20',
                      content: 'text-[#F5A524] text-[11px] font-medium'
                    }}
                  >
                    Top {popularityPercent.toFixed(1)}%
                  </Chip>
                )}
              </div>
            </div>
          </div>
        );
      }

      case 'offerDetails':
        return <RevshareDetailsCell offer={offer} />;

      case 'statistics':
        return <StatisticsCell offer={offer} />;

      case 'status': {
        const statusConfig = getStatusConfig(offerStatus);
        return (
          <Chip
            className="h-[24px] px-[4px] text-[12px] font-normal bg-zinc-100"
            style={{
              backgroundColor: '#F4F4F5',
              color: '#11181C',
            }}
          >
            <div className="flex items-center gap-[4px]">
              <div
                className="w-[7px] h-[7px] rounded-full"
                style={{ backgroundColor: statusConfig.dotColor }}
              />
              <span>{statusConfig.label}</span>
            </div>
          </Chip>
        );
      }

      case 'actions': {
        const canActivate = offerStatus !== 'active' && offerStatus !== 'refusal';
        const canDeactivate = offerStatus === 'active';

        return (
          <Dropdown>
            <DropdownTrigger>
              <Button
                variant="light"
                isIconOnly
                className="min-w-[32px] w-[32px] h-[32px]"
              >
                <Icon icon="solar:menu-dots-bold" width={20} height={20} />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Действия"
              onAction={(key: React.Key) => handleAction(String(key), offerId)}
            >
              {canActivate ? (
                <DropdownItem key="activate" className="text-success" color="success">
                  Активировать
                </DropdownItem>
              ) : canDeactivate ? (
                <DropdownItem key="deactivate" className="text-danger" color="danger">
                  Деактивировать
                </DropdownItem>
              ) : (
                <DropdownItem key="view">
                  Просмотр
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        );
      }

      default:
        return null;
    }
  };

  return (
    <UniversalTable
      data={offers}
      columns={columns}
      renderCell={renderCell}
      emptyContent="Нет доступных офферов RevShare"
    />
  );
};

export default DashboardRevShareOffersTable;
